/**
 * CSV export for a server-backed grid.
 *
 * Exporting a grid means answering the server the same question the screen
 * asked — same filters, same search, same order — but for every matching row
 * rather than one page, then writing the on-screen columns as text. This hook
 * owns that run end to end so a grid opts into export by declaring it, not by
 * hand-rolling another paging loop and another column list.
 *
 * Contains:
 * - useGridExport — { runExport, isExporting } for one grid
 */

import { useCallback, useState } from 'react';

import useDataGridConfig from '@sophocles/data-grids/config/useDataGridConfig';
import { fetchAllPages } from '@sophocles/data-grids/datasource/fetchAllPages';
import { downloadCsv, rowsToCsv } from '@sophocles/utils/csvExport';

import { buildExportColumns } from './buildExportColumns';

/**
 * Drives one grid's CSV export.
 *
 * @param {object} opts
 * @param {object[]} opts.specColumns - The descriptor's `columns` array.
 * @param {object} [opts.apiRef] - The grid's MUI X apiRef, read for the columns
 *   currently on screen and the order the user left them in.
 * @param {(page: { limit: number, offset: number }) => Promise<object|Array>} opts.fetchPage
 *   Page fetcher with the grid's live filter/sort/search params already bound.
 * @param {string} opts.fileName - Downloaded file name, e.g. `'customers.csv'`.
 * @param {object} [opts.overrides] - Per-field export overrides, see `buildExportColumns`.
 * @param {object[]} [opts.extraColumns] - Client-computed columns the descriptor
 *   does not own, see `buildExportColumns`.
 * @param {Object<string, string>} [opts.terms] - Tenant vocabulary for headers
 *   and enum labels, see `buildExportColumns`.
 * @param {number} [opts.pageSize] - Rows per request while paging. Pass the
 *   descriptor's `max_page_size` so the walk asks for what the endpoint will
 *   actually serve rather than a larger number it silently clamps.
 * @param {(error: Error) => void} [opts.onError] - Called when the run fails, so
 *   the grid can surface it; the error is logged either way.
 * @returns {{ runExport: () => Promise<void>, isExporting: boolean }}
 */
export default function useGridExport({
  specColumns,
  apiRef,
  fetchPage,
  fileName,
  overrides,
  extraColumns,
  terms,
  pageSize,
  onError,
}) {
  const config = useDataGridConfig();
  const [isExporting, setIsExporting] = useState(false);

  const runExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const columns = buildExportColumns(specColumns, {
        visibleFields: apiRef?.current?.getVisibleColumns?.().map(column => column.field),
        overrides,
        extraColumns,
        terms,
        translate: config.translate,
        formatters: config.formatters,
        resolveReference: config.resolveReference,
      });
      const rows = await fetchAllPages(fetchPage, pageSize ? { pageSize } : undefined);
      // A UTF-8 BOM so Excel reads accented names as written rather than as
      // mojibake — customer and site names carry them routinely.
      downloadCsv(fileName, rowsToCsv(columns, rows, { bom: true }));
    } catch (error) {
      console.error('Grid export failed:', error);
      onError?.(error);
    } finally {
      setIsExporting(false);
    }
  }, [
    specColumns,
    apiRef,
    fetchPage,
    fileName,
    overrides,
    extraColumns,
    terms,
    pageSize,
    onError,
    config.translate,
    config.formatters,
    config.resolveReference,
  ]);

  return { runExport, isExporting };
}
